import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, OnInit, output } from '@angular/core';
import { SubscriptionService } from '@app/+authenticated/+reports/shared/subscriptions/subscription.service';
import { BoldPipe } from '@app/pipes/bold.pipe';
import { LocaleDatePipe } from '@app/pipes/locale-date.pipe';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ButtonComponent } from '@sb/ui/button';
import { SbDialogSharedModule } from '@sb/ui/dialog';
import { IconComponent } from '@sb/ui/icon';
import { LaddaDirective } from '@sb/ui/ladda';
import { InvoicePeriodType, PlanQuotaModel, QuotaType, SubscriptionModel } from '@shiftbase-com/ts-shared/models';

const quotaTypesToFilterOut = [
  QuotaType.SEATS,
  QuotaType.ACTIVE_TIME_OFF_BALANCES,
  QuotaType.RATE_CARDS_WITH_MULTIPLE_TIME_BLOCKS,
  QuotaType.RATE_CARDS_WITH_TIME_SURCHARGES,
  QuotaType.ABSENCE_POLICIES,
  QuotaType.HOLIDAY_GROUPS,
];

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-confirm-downgrade',
  standalone: true,
  imports: [
    CommonModule,
    TranslatePipe,
    ButtonComponent,
    IconComponent,
    BoldPipe,
    LocaleDatePipe,
    LaddaDirective,
    SbDialogSharedModule,
  ],
  templateUrl: './confirm-downgrade.component.html',
})
export class ConfirmDowngradeComponent implements OnInit {
  private translate = inject(TranslateService);
  private subscriptionService = inject(SubscriptionService);

  selectedSubscription = input.required<SubscriptionModel>();
  isLoading = input(false);
  currentSubscription = input.required<SubscriptionModel>();

  cancelClick = output<void>();
  confirmClick = output<void>();

  confirmDowngradeSubtitleMessage: string;

  ngOnInit(): void {
    this.generateConfirmDowngradeSubtitleMessage();
  }

  generateConfirmDowngradeSubtitleMessage(): void {
    if (this.isChangingBillingCycleToMonthly && this.isDeactivatingIntegrationPlus) {
      if (this.selectedSubscription()?.plan.type === this.currentSubscription().plan.type) {
        this.confirmDowngradeSubtitleMessage = this.translate.instant(
          `Downgrade Plan to {{currentPlanName}} without App Center Plus (Monthly)`,
          {
            currentPlanName: this.currentSubscription().plan.name,
          },
        );
        return;
      }

      this.confirmDowngradeSubtitleMessage = this.translate.instant(
        `{{targetPlanName}} without App Center Plus (Monthly)`,
        { targetPlanName: this.selectedSubscription().plan.name },
      );
      return;
    }

    if (this.isChangingBillingCycleToMonthly) {
      if (this.selectedSubscription()?.plan.type === this.currentSubscription().plan.type) {
        this.confirmDowngradeSubtitleMessage = this.translate.instant(`{{currentPlanName}} (Monthly)`, {
          currentPlanName: this.currentSubscription().plan.name,
        });
        return;
      }

      this.confirmDowngradeSubtitleMessage = this.translate.instant(`{{targetPlanName}} (Monthly)`, {
        targetPlanName: this.selectedSubscription().plan.name,
      });
      return;
    }

    if (this.isDeactivatingIntegrationPlus) {
      if (this.selectedSubscription()?.plan.type === this.currentSubscription().plan.type) {
        this.confirmDowngradeSubtitleMessage = this.translate.instant(`{{currentPlanName}} without App Center Plus`, {
          currentPlanName: this.currentSubscription().plan.name,
        });
        return;
      }

      this.confirmDowngradeSubtitleMessage = this.translate.instant(`{{targetPlanName}} without App Center Plus`, {
        targetPlanName: this.selectedSubscription().plan.name,
      });
      return;
    }

    this.confirmDowngradeSubtitleMessage = this.selectedSubscriptionName;
  }

  get isChangingBillingCycleToMonthly(): boolean {
    return (
      this.subscriptionService.selectedSubscriptionValue?.invoicePeriod === InvoicePeriodType.MONTH &&
      this.currentSubscription().invoice_period !== InvoicePeriodType.MONTH
    );
  }

  get isDeactivatingIntegrationPlus(): boolean {
    return !this.subscriptionService.selectedSubscriptionValue?.integrationPlus;
  }

  get selectedSubscriptionName(): string {
    return this.selectedSubscription()?.plan?.name;
  }

  get quotaReached(): PlanQuotaModel[] {
    return this.selectedSubscription()?.quotaReached?.filter((quota) => !quotaTypesToFilterOut.includes(quota.type));
  }

  get overQuota(): boolean {
    return this.quotaReached?.length > 0;
  }

  get nextBillingDate() {
    return this.currentSubscription()?.next_billing_date;
  }
}
